import {computed, type ComputedRef} from 'vue';

import type {IUser} from '@/api/types/user';
import {ERoleSystem} from '@/api/enums/role';

import {translationRoleSystem} from '@/api/translations/role';
import {userDefault} from '@/api/defaults/user';

export const useUserPresenterHook = (item?: IUser | ComputedRef<IUser>) => {
  const getItem = computed(() => {
    return !item
      ? userDefault
      : !!(item as ComputedRef<IUser>).value
        ? (item as ComputedRef<IUser>).value
        : (item as IUser);
  });

  const getName = computed(() =>
    getItem.value.name ? getItem.value.name : 'н/д',
  );
  const getEmail = computed(() => getItem.value.email);
  const getUsername = computed(() => getItem.value.username ?? 'н/д');
  const getRoles = computed(() =>
    getItem.value.roles.map((item) => item.toLowerCase() as ERoleSystem),
  );
  const getRolesTranslate = computed(() =>
    getRoles.value.map((item) => translationRoleSystem[item]),
  );
  const getTechRoles = computed(() => getItem.value.tech_roles ?? []);
  const getTechRolesTranslate = computed(() =>
    getTechRoles.value.map(
      (item) => translationRoleSystem[item as ERoleSystem] ?? item,
    ),
  );
  const getViewRoles = computed(() => getItem.value.view_roles ?? []);
  const getViewRolesMap = computed(() => getItem.value.view_roles_map ?? []);
  const getViewRolesMapNames = computed(() =>
    getViewRoles.value.length > 0 ? getViewRoles.value.join(', ') : 'н/д',
  );

  const isGuest = computed(() => getRoles.value.includes(ERoleSystem.GUEST));
  const isClient = computed(() => getRoles.value.includes(ERoleSystem.USER));
  const isAdmin = computed(() => getRoles.value.includes(ERoleSystem.ADMIN));

  const hasAccessByRole = (roles?: ERoleSystem[]) => {
    const userRolesSet = new Set(getRoles.value);
    const accessRolesSet = new Set(roles ?? []);

    const rolesIntersection = new Set(
      [...accessRolesSet].filter((item) => userRolesSet.has(item)),
    );

    return !!rolesIntersection.size;
  };

  return {
    getItem,

    getName,
    getEmail,
    getUsername,
    getRoles,
    getRolesTranslate,
    getTechRoles,
    getTechRolesTranslate,
    getViewRoles,
    getViewRolesMap,
    getViewRolesMapNames,

    isAdmin,
    isGuest,
    isClient,

    hasAccessByRole,
  };
};
